import { nanoid } from "nanoid";
import { colors } from "widgets/utils";

import { refreshTracks } from "../../main-canvas/messages/post-track-messages.ts";
import { TrackEnum, TrackTextEnum } from "../../openapi";
import TrackWorkerGene from "../../track-workers/gene-track-worker.ts?worker";
import TrackWorkerNUFeature from "../../track-workers/nufeature-track-worker.ts?worker";
import TrackWorkerProtein from "../../track-workers/protein-track-worker.ts?worker";
import TrackWorkerSequence from "../../track-workers/sequence-track-worker.ts?worker";
import {
  type RenderingOptions,
  type SubType,
  type Track,
  type TrackGroup,
  type TrackGroups,
  type Tracks,
  type TrackType,
} from "../../types.ts";
import { getColorByTrackType } from "../color-conversion.ts";

interface OtherTrackOptions {
  color: string;
  text: RenderingOptions["text"];
  isVisible: boolean;
  trackId?: string;
}

export interface NUFeatureOptions extends OtherTrackOptions {
  subType: SubType;
}

interface BaseTrackConfig {
  trackType: TrackType;
  group: string | { genomeId: number; contigId: number; toBase?: number };
}

export interface NUFeatureTrackConfig extends BaseTrackConfig {
  trackType: typeof TrackEnum.NUCLEOTIDE_FEATURE;
  options: NUFeatureOptions;
}

export interface OtherTrackConfig extends BaseTrackConfig {
  trackType: Exclude<TrackType, typeof TrackEnum.NUCLEOTIDE_FEATURE>;
  options?: OtherTrackOptions;
}

export type TrackConfig = NUFeatureTrackConfig | OtherTrackConfig;

// Pre-calculate common values
const DEFAULT_TRACK_CONFIG: Omit<Track, "trackType" | "trackId" | "groupId" | "worker"> = {
  width: 0,
  height: 0,
  text: TrackTextEnum.INNER,
  color: colors.primaryPale,
  synteny: true,
  baseStart: 0,
  isVisible: true,
  isLoaded: false,
  isOrthologsLoaded: false,
} as const;

export function createNewTracks<T extends Tracks, G extends TrackGroups, N = { trackId: string; groupId: string }>(
  tracksToCreate: TrackConfig[],
  {
    tracks,
    trackGroups,
    organisms,
  }: {
    tracks: T;
    trackGroups: G;
    organisms: Map<
      number,
      { id: number; name: string; contigs: Map<number, { id: number; length: number; name: string }> }
    >;
  },
  setupWorker: (worker: Worker) => void,
  afterGroupOrTrackId?: string,
): { trackGroups: G; tracks: T; newTrackSet: Set<N> } {
  let newTrackGroups = new Map(trackGroups) as G;
  const newTrackSet = new Set<N>();

  for (const { trackType, group, options } of tracksToCreate) {
    const trackId = options?.trackId ?? nanoid();
    const groupId = typeof group === "string" ? group : nanoid();
    let organism;
    let contig;

    // Determine group and contig information
    if (typeof group === "string") {
      // Use existing group
      const existingGroup = newTrackGroups.get(groupId);
      if (existingGroup) {
        organism = organisms.get(existingGroup.organismId);
        contig = organism?.contigs.get(existingGroup.contigId);
      }
    } else {
      // Create a new group
      organism = organisms.get(group.genomeId);
      contig = organism?.contigs.get(group.contigId);
    }

    if (!(organism && contig)) {
      continue; // Skip this track and continue with the next
    }

    // Create appropriate worker based on track type
    const trackWorker = getWorkerByTrackType(trackType);

    if (!trackWorker) {
      continue; // Skip this track and continue with the next
    }

    // Set up worker message handler
    // setupWorkerMessageHandler(trackWorker, set, get);
    setupWorker(trackWorker);

    // Create track object
    const track: Track = {
      ...DEFAULT_TRACK_CONFIG,
      trackType,
      trackId,
      groupId,
      worker: trackWorker,
      subType: (options as NUFeatureOptions | undefined)?.subType,
      color: options?.color ?? getColorByTrackType(trackType),
      text: options?.text ?? DEFAULT_TRACK_CONFIG.text,
      isVisible: options?.isVisible ?? DEFAULT_TRACK_CONFIG.isVisible,
    };

    // Send initial render message to worker
    const trackGroup = newTrackGroups.get(groupId);
    if (trackGroup) {
      // TODO Sure of that?
      refreshTracks();
    }

    // Add track to its group
    if (typeof group === "string") {
      if (!trackGroup?.trackIds.includes(track.trackId)) {
        trackGroup?.trackIds.push(track.trackId);
      }
    } else {
      const newGroup = {
        groupId,
        contigId: contig?.id,
        contigName: contig?.name || "",
        organismId: group.genomeId,
        organismName: organism?.name || "",
        trackIds: [track.trackId],
        graphics: {
          baseShift: group.toBase ?? 0,
          isVisible: true,
          length: contig.length,
        },
      } satisfies TrackGroup;

      if (afterGroupOrTrackId && newTrackGroups.has(afterGroupOrTrackId)) {
        const updatedTrackGroups = new Map() as G;

        for (const [key, group] of newTrackGroups) {
          updatedTrackGroups.set(key, group);

          if (key === afterGroupOrTrackId) {
            updatedTrackGroups.set(groupId, newGroup);
          }
        }

        newTrackGroups = updatedTrackGroups;
      } else {
        // Just add it at the end
        newTrackGroups.set(groupId, newGroup);
      }
    }

    // Add track to tracks collection
    newTrackSet.add({ trackId: track.trackId, groupId: track.groupId } as N);
    tracks.set(track.trackId, track);
  }

  return { trackGroups: newTrackGroups, tracks, newTrackSet };
}
const getWorkerByTrackType = (trackType: TrackType) => {
  switch (trackType) {
    case TrackEnum.PROTEIN_LOCATION:
      return new TrackWorkerProtein();
    case TrackEnum.GENE:
      return new TrackWorkerGene();
    case TrackEnum.NUCLEOTIDE_FEATURE:
      return new TrackWorkerNUFeature();
    case TrackEnum.SEQUENCE:
      return new TrackWorkerSequence();
    default:
      return undefined;
  }
};
